import METRIC_BUDGETS from '../config/metricBudgets';

/**
 * Get performance status (icon and class) based on FPS values
 * Uses unified icon system: check (good square), ! (warning triangle), stop (critical)
 * Severity-badge styles are in Heatmaps.css
 */
export function getPerformanceStatus(avgFps, lowFps) {
  const avgScore = avgFps >= 45 ? 0 : avgFps >= 35 ? 1 : 2;
  const lowScore = lowFps >= 30 ? 0 : lowFps >= 20 ? 1 : 2;
  const finalScore = Math.max(avgScore, lowScore);

  if (finalScore === 0) return { emoji: '\u2713', className: 'severity-badge good', label: 'Good' };
  if (finalScore === 1) return { emoji: '!', className: 'severity-badge medium', label: 'Warning' };
  return { emoji: '\uD83D\uDED1', className: 'severity-badge critical', label: 'Critical' };
}

/**
 * Safely parse timestamp from capture file
 * Handles formats like: "2026-01-14_22-29-02" or ISO strings
 * Timestamps are assumed to be in UTC
 */
export function parseTimestamp(timestamp) {
  if (!timestamp) return null;
  try {
    // Try ISO format first (already handles timezone)
    let date = new Date(timestamp);
    if (!isNaN(date.getTime())) return date;

    // Try replacing underscore with 'T' and adding Z for UTC
    date = new Date(timestamp.replace('_', 'T') + 'Z');
    if (!isNaN(date.getTime())) return date;

    // Try parsing "YYYY-MM-DD_HH-mm-ss" format as UTC
    const match = timestamp.match(/(\d{4})-(\d{2})-(\d{2})[_T](\d{2})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day, hour, min, sec] = match;
      // Use Date.UTC to treat the timestamp as UTC
      return new Date(Date.UTC(year, month - 1, day, hour, min, sec));
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Format area name for display
 * Removes "ZoneAuthoring" suffix and replaces underscores with spaces
 */
export function formatAreaName(name) {
  if (!name) return '';
  return name
    .replace(/ZoneAuthoring$/i, '')
    .replace(/_/g, ' ')
    .trim();
}

/**
 * Extract the bare zone key from either naming convention:
 * - Areas: "Gamiira01_DunmiirFTUEZoneAuthoring" -> "Gamiira01_DunmiirFTUE"
 * - STF:   "SO_Zone_Gamiira01_DunmiirFTUE"      -> "Gamiira01_DunmiirFTUE"
 */
export function bareZoneKey(name) {
  if (!name) return '';
  return name.replace(/^SO_Zone_/, '').replace(/ZoneAuthoring$/, '');
}

/**
 * Convert STF zone name to the map config name used in GCS
 * "SO_Zone_Gamiira01_DunmiirFTUE" -> "Gamiira01_DunmiirFTUEZoneAuthoring"
 */
export function stfZoneToMapName(stfZoneName) {
  return stfZoneName.replace(/^SO_Zone_/, '') + 'ZoneAuthoring';
}

/**
 * Convert yaw degrees to a Unity quaternion (Y-axis rotation)
 */
export function yawToQuaternion(yawDegrees) {
  const rad = (yawDegrees || 0) * Math.PI / 180;
  return { x: 0, y: Math.sin(rad / 2), z: 0, w: Math.cos(rad / 2) };
}

/**
 * Transform an STF scan point to the capturePoint format used by HeatmapViewer
 */
export function stfPointToCapturePoint(p) {
  return {
    position: { x: p.pos_x, y: p.pos_y, z: p.pos_z },
    rotation: yawToQuaternion(p.yaw_degrees),
    framerate: p.fps > 0 ? p.fps : null,
    gpuTime: p.gpu_time > 0 ? p.gpu_time : null,
    cpuTime: p.cpu_time > 0 ? p.cpu_time : null,
    drawCalls: p.draw_calls,
    batches: p.batches,
    triangles: p.triangles,
    vertices: p.vertices,
    visibleRenderers: p.visible_renderers,
    candidateRenderers: p.candidate_renderers,
    candidateTriangles: p.candidate_triangles,
    setPassCalls: p.set_pass_calls,
    shadowCasterRenderers: p.shadow_caster_renderers,
    candidateLights: p.candidate_lights,
    candidateShadowCastingLights: p.candidate_shadow_casting_lights,
    candidateMeshRenderers: p.candidate_mesh_renderers,
    visibleMeshRenderers: p.visible_mesh_renderers,
    candidateSkinnedMeshRenderers: p.candidate_skinned_mesh_renderers,
    visibleSkinnedMeshRenderers: p.visible_skinned_mesh_renderers,
    candidateParticleRenderers: p.candidate_particle_renderers,
    visibleParticleRenderers: p.visible_particle_renderers,
    candidateOtherRenderers: p.candidate_other_renderers,
    visibleOtherRenderers: p.visible_other_renderers,
    batchedDraws: p.batched_draws,
    unbatchedDraws: p.unbatched_draws,
    distinctShaderVariants: p.distinct_shader_variants,
    depthPrepassDraws: p.depth_prepass_draws,
    mainLightShadowDraws: p.main_light_shadow_draws,
    additionalLightShadowDraws: p.additional_light_shadow_draws,
    opaqueDraws: p.opaque_draws,
    transparentDraws: p.transparent_draws,
    skyboxDraws: p.skybox_draws,
    postFxDraws: p.post_fx_draws,
    cullingRatio: p.candidate_renderers > 0 ? (p.visible_renderers ?? 0) / p.candidate_renderers : null,
    _source: 'stf-scan',
    _device: 'Editor',
    _quality: 'Scene Scan',
    _isSTFScan: true,
    _yawDegrees: p.yaw_degrees ?? null,
  };
}

/**
 * Format a metric value for display (e.g. 1234567 -> "1.2M", 45000 -> "45.0K")
 */
export function formatMetricValue(v) {
  if (v == null || v < 0) return 'n/a';
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + 'M';
  if (v >= 10_000) return (v / 1_000).toFixed(1) + 'K';
  return String(Math.round(v));
}

/**
 * Normalize a zone name for fuzzy matching
 */
export function normalizeForMatch(s) {
  return s.toLowerCase().replace(/[-_]/g, '').replace(/(zone|level)authoring$/i, '');
}

/**
 * Check if a zone name matches a search query (bidirectional substring)
 */
export function matchesArea(zoneName, query) {
  const z = normalizeForMatch(zoneName);
  const q = normalizeForMatch(query);
  return z.includes(q) || q.includes(z);
}

/**
 * LOD (Level of Detail) configuration for BigQuery heatmap queries
 */
export const LOD_LEVELS = [
  { level: 0, cellSize: 10,  maxRatio: Infinity, minRatio: 0.4, label: 'Medium' },    // 10m cells
  { level: 1, cellSize: 2,   maxRatio: 0.4,      minRatio: 0.1, label: 'Detail' },    // 2m cells
  { level: 2, cellSize: null, maxRatio: 0.1,     minRatio: 0,   label: 'Full' },      // Individual points
];

/**
 * Point budget configuration for dynamic LOD selection
 */
export const POINT_BUDGET = {
  IDEAL_POINTS: 2000,
  MAX_POINTS: 100000,
  enabled: true
};

/**
 * Get LOD level from zoom ratio (effectiveSize / baseFrustumSize)
 */
export function getLODFromRatio(ratio) {
  for (const lod of LOD_LEVELS) {
    if (ratio >= lod.minRatio && ratio < lod.maxRatio) {
      return lod;
    }
  }
  return LOD_LEVELS[0];
}

/**
 * Calculate optimal LOD level considering both zoom ratio and point budget
 */
export function calculateOptimalLOD(zoomRatio, currentPointCount, lodCacheRef) {
  const zoomBasedLOD = getLODFromRatio(zoomRatio);
  const maxLOD = LOD_LEVELS.length - 1;

  if (!POINT_BUDGET.enabled || currentPointCount == null || !lodCacheRef?.current) {
    return { level: zoomBasedLOD.level, reason: 'zoom' };
  }

  const currentLODData = lodCacheRef.current.get(zoomBasedLOD.level);
  const currentLODCount = currentLODData?.cellCount || currentLODData?.totalPoints || currentPointCount;

  // Below ideal? Try finer LOD to get more detail
  if (currentLODCount < POINT_BUDGET.IDEAL_POINTS && zoomBasedLOD.level < maxLOD) {
    for (let finerLevel = zoomBasedLOD.level + 1; finerLevel <= maxLOD; finerLevel++) {
      const finerData = lodCacheRef.current.get(finerLevel);
      if (finerData) {
        const finerCount = finerData.cellCount || finerData.totalPoints || 0;
        if (finerCount <= POINT_BUDGET.MAX_POINTS) {
          return { level: finerLevel, reason: 'budget_low' };
        }
      }
    }
  }

  // Above max? Try coarser LOD to reduce load
  if (currentLODCount > POINT_BUDGET.MAX_POINTS && zoomBasedLOD.level > 0) {
    const coarserLevel = zoomBasedLOD.level - 1;
    const coarserData = lodCacheRef.current.get(coarserLevel);
    if (coarserData) {
      const coarserCount = coarserData.cellCount || coarserData.totalPoints || 0;
      if (coarserCount > 0) {
        return { level: coarserLevel, reason: 'budget_high' };
      }
    }
  }

  return { level: zoomBasedLOD.level, reason: 'zoom' };
}

/**
 * Check if LOD should auto-upgrade based on point budget.
 * Returns upgrade info { level, points } if upgrade recommended, null otherwise.
 *
 * @param {number} currentLOD - Current LOD level
 * @param {number} currentPoints - Current total point count
 * @param {Array|Object} lodCacheRefs - Single cache ref or array of cache refs (for CompareMode)
 * @returns {{ level: number, points: number } | null}
 */
export function getAutoUpgradeLOD(currentLOD, currentPoints, lodCacheRefs) {
  if (!POINT_BUDGET.enabled) return null;

  const maxLOD = LOD_LEVELS.length - 1;

  if (currentLOD >= maxLOD) {
    return null;
  }

  const caches = Array.isArray(lodCacheRefs) ? lodCacheRefs : [lodCacheRefs];

  let bestUpgrade = null;
  for (let finerLevel = currentLOD + 1; finerLevel <= maxLOD; finerLevel++) {
    const allData = caches.map(ref => ref.current?.get(finerLevel));

    if (allData.every(d => d != null)) {
      const finerPoints = allData.reduce((sum, d) => sum + (d.totalPoints || 0), 0);

      if (finerPoints <= POINT_BUDGET.MAX_POINTS) {
        bestUpgrade = { level: finerLevel, points: finerPoints };
      }
    } else {
      break;
    }
  }

  return bestUpgrade;
}

// File limit options for the dropdown
export const FILE_LIMIT_OPTIONS = [1, 2, 5, 10, 20, 50];

// Date range options for the filter dropdown
export const DATE_RANGE_OPTIONS = [
  { value: 1, label: 'Last 24 hours' },
  { value: 2, label: 'Last 2 days' },
  { value: 3, label: 'Last 3 days' },
  { value: 7, label: 'Last 7 days' },
  { value: 14, label: 'Last 14 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 365, label: 'Last year' },
  { value: 0, label: 'All time' },
  { value: 'custom', label: 'Custom range' },
];

// Metrics used in Hotspots & Summary analysis panels
export const ANALYSIS_METRICS = [
  { key: 'drawCalls', label: 'Draw Calls', budget: METRIC_BUDGETS.drawCalls },
  { key: 'batches', label: 'Batches', budget: METRIC_BUDGETS.batches },
  { key: 'triangles', label: 'Triangles', budget: METRIC_BUDGETS.triangles },
  { key: 'vertices', label: 'Vertices', budget: METRIC_BUDGETS.vertices },
];
